import axios from 'axios';
import { validate as isUuid } from 'uuid';
import { mockInitData } from 'templates/initData';
import { getToken, setToken, clearToken, hasValidToken, extractAndStoreToken } from './tokenManager';

const axiosServices = axios.create({
  baseURL: process.env.REACT_APP_USE_MOCK_INIT_DATA === 'true' ? process.env.REACT_APP_API_URL_TEST : process.env.REACT_APP_API_URL,
  // withCredentials: true, // Include HttpOnly cookies in requests
  timeout: 30000 // 30 seconds
});

let isFlushing = false;
let isRefreshing = false;
let failedQueue = [];
let refreshPromise = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else {
      prom.originalRequest.headers.Authorization = `Bearer ${token}`;
      prom.resolve(axiosServices(prom.originalRequest));
    }
  });
  failedQueue = [];
};

function saveErrorLocally(log) {
  const sanitized = {
    endpoint: log.endpoint,
    method: log.method,
    status: log.status,
    time: log.time,
    error: log.error?.message ? log.error.message.slice(0, 100) : null
  };

  const existing = JSON.parse(sessionStorage.getItem('pendingLogs') || '[]');
  if (existing.length < 50) {
    existing.push(sanitized);
    sessionStorage.setItem('pendingLogs', JSON.stringify(existing));
  }
}

async function sendErrorLog(log) {
  try {
    await axiosServices.post('/api/log-client-error', log);
  } catch {
    saveErrorLocally(log);
  }
}

async function flushPendingLogs() {
  if (isFlushing) return;

  const pending = JSON.parse(localStorage.getItem('pendingLogs') || '[]');
  if (!pending.length) return;

  isFlushing = true;
  try {
    await axiosServices.post('/api/log-client-error/batch', pending);
    localStorage.removeItem('pendingLogs');
  } catch (err) {
    const status = err?.response?.status;
    if (status >= 400 && status < 500) {
      localStorage.removeItem('pendingLogs');
    }
  } finally {
    isFlushing = false;
  }
}

export const forceRefreshToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const useMockData = process.env.REACT_APP_USE_MOCK_INIT_DATA === 'true';
      const initData = useMockData ? mockInitData.initDataPlayer1 : window.Telegram?.WebApp?.initData;

      if (!initData) throw new Error('No initData available for refresh');

      const { data } = await axios.post(
        '/api/initdata',
        { initdata: initData },
        {
          baseURL: axiosServices.defaults.baseURL
          // withCredentials: true // Include cookies
        }
      );

      // Store token in memory and set Authorization header
      extractAndStoreToken(data);
      axiosServices.defaults.headers.common.Authorization = `Bearer ${data.token}`;

      return data.token;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

axiosServices.interceptors.response.use(
  async (response) => {
    await flushPendingLogs();
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status || 0;

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ originalRequest, resolve, reject });
        });
      }

      isRefreshing = true;

      try {
        const token = await forceRefreshToken();
        processQueue(null, token);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosServices(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearToken(); // Clear from memory
        delete axiosServices.defaults.headers.common.Authorization;
        // Backend will also clear HttpOnly cookie
        window.location.reload();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } else {
      const endpoint = error?.config?.url || 'unknown';
      const method = (error?.config?.method || 'UNKNOWN').toUpperCase();

      const log = {
        endpoint,
        method,
        error,
        status,
        time: new Date().toISOString()
      };
      sendErrorLog(log);
    }

    return Promise.reject(error?.response?.data.message || 'Something went wrong');
  }
);

export default axiosServices;
